import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ComponentBatchIngredient,
  ComponentBatchIngredientsState,
  CreateComponentBatchIngredientRequest,
  UpdateComponentBatchIngredientRequest
} from '../../types/componentBatchIngredient';
import { componentBatchIngredientApiService } from '../../services/componentBatchIngredientApiService';

// Async thunks

/**
 * Fetch all component batch ingredients from the API
 */
export const fetchAllComponentBatchIngredients = createAsyncThunk(
  'componentBatchIngredients/fetchAll',
  async () => {
    return await componentBatchIngredientApiService.fetchAllComponentBatchIngredients();
  }
);

/**
 * Fetch component batch ingredients for a specific component batch
 */
export const fetchComponentBatchIngredientsByComponentBatchId = createAsyncThunk(
  'componentBatchIngredients/fetchByComponentBatchId',
  async (componentBatchId: string) => {
    return await componentBatchIngredientApiService.fetchComponentBatchIngredientsByComponentBatchId(componentBatchId);
  }
);

/**
 * Create a new component batch ingredient
 */
export const createComponentBatchIngredient = createAsyncThunk(
  'componentBatchIngredients/create',
  async (ingredient: CreateComponentBatchIngredientRequest) => {
    return await componentBatchIngredientApiService.createComponentBatchIngredient(ingredient);
  }
);

/**
 * Update an existing component batch ingredient
 */
export const updateComponentBatchIngredient = createAsyncThunk(
  'componentBatchIngredients/update',
  async ({ id, data }: { id: string; data: UpdateComponentBatchIngredientRequest }) => {
    return await componentBatchIngredientApiService.updateComponentBatchIngredient(id, data);
  }
);

/**
 * Delete a component batch ingredient
 */
export const deleteComponentBatchIngredient = createAsyncThunk(
  'componentBatchIngredients/delete',
  async (id: string) => {
    await componentBatchIngredientApiService.deleteComponentBatchIngredient(id);
    return id;
  }
);

// Initial state
const initialState: ComponentBatchIngredientsState = {
  ingredients: [],
  loading: false,
  error: null,
};

const componentBatchIngredientSlice = createSlice({
  name: 'componentBatchIngredients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all component batch ingredients
    builder
      .addCase(fetchAllComponentBatchIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllComponentBatchIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload;
      })
      .addCase(fetchAllComponentBatchIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch component batch ingredients';
      });

    // Fetch by component batch ID
    builder
      .addCase(fetchComponentBatchIngredientsByComponentBatchId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComponentBatchIngredientsByComponentBatchId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace ingredients for this component batch
        const componentBatchId = action.payload[0]?.componentBatchId;
        if (componentBatchId) {
          state.ingredients = [
            ...state.ingredients.filter(i => i.componentBatchId !== componentBatchId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchComponentBatchIngredientsByComponentBatchId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch component batch ingredients';
      });

    // Create component batch ingredient
    builder
      .addCase(createComponentBatchIngredient.fulfilled, (state, action) => {
        state.ingredients.push(action.payload);
      })
      .addCase(createComponentBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create component batch ingredient';
      });

    // Update component batch ingredient
    builder
      .addCase(updateComponentBatchIngredient.fulfilled, (state, action) => {
        const index = state.ingredients.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.ingredients[index] = action.payload;
        }
      })
      .addCase(updateComponentBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update component batch ingredient';
      });

    // Delete component batch ingredient
    builder
      .addCase(deleteComponentBatchIngredient.fulfilled, (state, action) => {
        state.ingredients = state.ingredients.filter(i => i.id !== action.payload);
      })
      .addCase(deleteComponentBatchIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete component batch ingredient';
      });
  }
});

export const { clearError } = componentBatchIngredientSlice.actions;
export default componentBatchIngredientSlice.reducer;
